import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BrainCircuit, RefreshCw, Loader2, Settings2, Sparkles, History } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import SignalCard, { TYPE_LABEL_NL } from "../components/assistant/SignalCard";
import AssistantSettingsPanel from "../components/assistant/AssistantSettingsPanel";
import { useT, locale } from "../lib/i18n";

export default function AssistantPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "signals";
  const setTab = (t) => { const n = new URLSearchParams(params); n.set("tab", t); setParams(n, { replace: true }); };
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState(null);
  const [running, setRunning] = useState(false);
  const [flash, setFlash] = useState("");

  const load = useCallback(async (refresh = false) => {
    const [s, st, p] = await Promise.all([
      api.get(`/assistant/signals${refresh ? "?refresh=true" : ""}`), api.get("/assistant/settings"), api.get("/assistant/profile"),
    ]);
    setData(s.data); setSettings(st.data); setProfile(p.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "history") api.get("/assistant/history").then((r) => setHistory(r.data)); }, [tab, data]);

  const runNow = async () => {
    setRunning(true);
    try { await load(true); setFlash(t("Cyclus uitgevoerd — signalen zijn bijgewerkt.")); setTimeout(() => setFlash(""), 3000); }
    finally { setRunning(false); }
  };

  const onFeedback = async (signal, action) => {
    setData((d) => ({ ...d, signals: d.signals.filter((s) => s.id !== signal.id) }));
    const words = { acted: "Genoteerd als actie — dit type krijgt meer gewicht.", dismissed: "Afgewezen — dit type wordt minder prominent.", snoozed: "Gesnoozed voor 3 dagen.", adjusted: "Drempel aangepast voor dit type." };
    setFlash(words[action] ? t(words[action]) : "");
    setTimeout(() => setFlash(""), 3000);
    api.get("/assistant/profile").then((r) => setProfile(r.data));
  };

  const signals = data?.signals || [];
  const tabs = [
    { k: "signals", l: `${t("Signalen")}${signals.length ? ` (${signals.length})` : ""}`, icon: Sparkles },
    { k: "settings", l: t("Instellingen & leren"), icon: Settings2 },
    { k: "history", l: t("Historie"), icon: History },
  ];

  return (
    <Layout
      title="AI Assistant"
      subtitle={t("Proactieve signalen · leert van hoe jij reageert")}
      right={
        <button onClick={runNow} disabled={running} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="assistant-run-now">
          {running ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {t("Nu doorlopen")}
        </button>
      }
    >
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200 overflow-x-auto whitespace-nowrap" data-testid="assistant-tabs">
        {tabs.map((tb) => (
          <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`assistant-tab-${tb.k}`}
            className={`px-4 h-10 text-sm font-bold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === tb.k ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <tb.icon size={13} /> {tb.l}
          </button>
        ))}
        {data?.last_run_at && <span className="ml-auto text-[11px] text-slate-400 font-mono-tab" data-testid="assistant-last-run">{t("laatste cyclus")} {new Date(data.last_run_at).toLocaleString(locale(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {t("toon")} {data.tone === "brief" ? t("kort") : t("uitgebreid")}</span>}
      </div>

      {flash && <div className="mb-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="assistant-flash">{flash}</div>}

      {tab === "signals" && (
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
          <div className="space-y-3" data-testid="assistant-signals-list">
            {!data && <div className="text-sm text-slate-500">{t("Laden…")}</div>}
            {data && signals.length === 0 && (
              <div className="pm-card p-10 text-center" data-testid="assistant-empty">
                <BrainCircuit className="mx-auto mb-3 text-emerald-500" size={40} />
                <div className="font-display font-black text-xl">{t("Alles onder controle")}</div>
                <div className="text-sm text-slate-500 mt-1">{t("Geen openstaande signalen. De assistent draait weer om {x}.", { x: settings?.run_time })}</div>
              </div>
            )}
            {signals.map((s) => <SignalCard key={s.id} signal={s} onFeedback={onFeedback} />)}
          </div>
          <div className="space-y-4">
            <div className="pm-card p-5" data-testid="assistant-explainer">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">{t("Hoe dit werkt")}</div>
              <ul className="text-xs text-slate-600 space-y-1.5 list-disc pl-4">
                <li>{t("Elke cyclus loopt de assistent agenda, inbox, clientcontacten, cases en contracten door.")}</li>
                <li>{t("Alleen de belangrijkste punten, gerangschikt op urgentie × impact × jouw geleerde gewicht.")}</li>
                <li><b>{t("Actie")}</b> → {t("type wordt scherper geprioriteerd.")} <b>{t("Niet relevant")}</b>/<b>{t("genegeerd")}</b> → {t("minder prominent.")} <b>{t("Te vroeg")}</b> → {t("drempel schuift op.")}</li>
                <li>{t("Signalen die 3 cycli blijven staan zonder reactie tellen als genegeerd.")}</li>
              </ul>
            </div>
            {profile && (
              <div className="pm-card p-5" data-testid="assistant-weights-mini">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">{t("Jouw gewichten")}</div>
                {Object.entries(profile.types).map(([ty, p]) => (
                  <div key={ty} className="flex items-center gap-2 text-xs py-1">
                    <span className="w-28 truncate">{t(TYPE_LABEL_NL[ty] || ty)}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, p.weight / 2 * 100)}%` }} /></div>
                    <span className="font-mono-tab w-10 text-right">{p.weight.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "settings" && settings && profile && (
        <AssistantSettingsPanel settings={settings} profile={profile} onSaved={(s) => setSettings(s)} />
      )}

      {tab === "history" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="assistant-history">
          <div className="pm-card p-5">
            <div className="font-display font-black text-sm mb-3">{t("Jouw reacties")}</div>
            {(history?.feedback || []).length === 0 && <div className="text-xs text-slate-500">{t("Nog geen feedback gegeven.")}</div>}
            <div className="space-y-1.5">
              {(history?.feedback || []).map((f) => (
                <div key={f.id} className="flex items-center gap-2 text-xs">
                  <span className={`pm-pill text-[10px] ${f.action === "acted" ? "pm-pill-green" : f.action === "dismissed" ? "pm-pill-red" : "pm-pill-slate"}`}>{f.action}</span>
                  <span className="font-semibold">{t(TYPE_LABEL_NL[f.type] || f.type)}</span>
                  {f.reason && <span className="text-slate-400">· {f.reason}</span>}
                  <span className="ml-auto text-slate-400 font-mono-tab">{new Date(f.at).toLocaleString(locale(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="pm-card p-5">
            <div className="font-display font-black text-sm mb-3">{t("Cycli")}</div>
            <div className="space-y-1.5">
              {(history?.runs || []).map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-xs">
                  <span className="pm-pill pm-pill-slate text-[10px]">{r.reason}</span>
                  <span>{r.candidates} {t("kandidaten")} · {r.new} {t("nieuw")} · {t("toon")} {r.tone}</span>
                  <span className="ml-auto text-slate-400 font-mono-tab">{new Date(r.ran_at).toLocaleString(locale(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
